import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';

import { ModuloAdminService } from '@/core/api/sicom-api/portal/modulo/modulo-admin.service';
import { ModuloAdmin } from '@/core/api/sicom-api/portal/modulo/modulo-admin.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-modulo-detalhe',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, DialogModule, ButtonModule, InputTextModule, CanDirective],
    templateUrl: './modulo-detalhe.html'
})
/** Modal de edição de nome/descrição de um módulo do portal (WEB_MODULO). Habilitar/desabilitar por UF é feito na tela de Módulos, não aqui. */
export class ModuloDetalhe {
    private readonly fb = inject(FormBuilder);
    private readonly service = inject(ModuloAdminService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);

    /** Visibilidade do modal (two-way). */
    readonly visivel = model<boolean>(false);
    /** Módulo em edição. */
    readonly modulo = input<ModuloAdmin | null>(null);
    /** Emite após salvar com sucesso (para a lista recarregar). */
    readonly salvo = output<void>();

    protected readonly roleModulosEditar = PERMISSOES.configuracoes.modulos.editar;

    protected readonly salvando = signal(false);

    protected readonly form = this.fb.nonNullable.group({
        nmModulo: ['', [Validators.required, Validators.maxLength(50)]],
        dsModulo: ['', [Validators.required, Validators.maxLength(250)]]
    });

    constructor() {
        // Preenche o form sempre que o modal abre com o módulo selecionado.
        effect(() => {
            if (!this.visivel()) {
                return;
            }
            const item = this.modulo();
            this.form.reset({ nmModulo: item?.nmModulo ?? '', dsModulo: item?.dsModulo ?? '' });
        });
    }

    protected salvar(): void {
        const item = this.modulo();
        if (this.form.invalid || !item) {
            this.form.markAllAsTouched();
            return;
        }
        this.salvando.set(true);
        const v = this.form.getRawValue();
        this.service
            .editar(item.idWebModulo, { nmModulo: v.nmModulo, dsModulo: v.dsModulo })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Módulo salvo', detail: 'O módulo foi salvo com sucesso.' });
                    this.salvando.set(false);
                    this.salvo.emit();
                    this.visivel.set(false);
                },
                error: () => {
                    this.salvando.set(false);
                }
            });
    }

    protected fechar(): void {
        this.visivel.set(false);
    }
}
